import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Loader2, ArrowLeft, Download, FileText, User, Phone, Mail, Clock } from "lucide-react";

export default function AdminOrderDetail() {
  const { id } = useParams();
  const [data, setData] = useState(null);
  const [statuses, setStatuses] = useState([]);
  const [newStatus, setNewStatus] = useState("");
  const [note, setNote] = useState("");
  const [assignee, setAssignee] = useState("");
  const [attach, setAttach] = useState("");
  const [saving, setSaving] = useState(false);

  const load = () => {
    api.get(`/admin/orders/${id}`).then((r) => {
      setData(r.data);
      setNewStatus(r.data.order?.fulfilmentStatus || "");
      setAssignee(r.data.order?.counsellorAssigned || "");
    });
    api.get("/admin/orders?limit=1").then((r) => setStatuses(r.data.statuses || []));
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [id]);

  const save = async (patch) => {
    setSaving(true);
    try {
      const { data: r } = await api.patch(`/admin/orders/${id}`, patch);
      setData((d) => ({ ...d, order: r.order }));
      toast.success("Updated");
      setNote(""); setAttach("");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Update failed");
    } finally { setSaving(false); }
  };

  if (!data) return <div className="flex justify-center py-24"><Loader2 className="h-6 w-6 animate-spin text-slate-400" /></div>;
  const o = data.order;
  const lead = data.lead;

  return (
    <div data-testid="admin-order-detail" className="max-w-4xl">
      <Link to="/admin/orders" className="inline-flex items-center gap-1 text-sm text-slate-500 hover:text-slate-800"><ArrowLeft className="h-4 w-4" /> Back to orders</Link>
      <h1 className="mt-2 font-display text-2xl font-extrabold text-slate-900">{o.planLabel}</h1>
      <p className="mt-1 text-xs font-mono text-slate-500">{o.orderId}</p>

      <div className="mt-6 grid gap-6 md:grid-cols-2">
        <div className="rounded-2xl border border-slate-200 bg-white p-5">
          <h2 className="text-xs font-bold uppercase tracking-wide text-slate-500">Order</h2>
          <dl className="mt-3 space-y-2 text-sm">
            <Row k="Product" v={o.planLabel} />
            <Row k="Amount" v={`₹${o.amount}`} />
            <Row k="Payment Status" v={o.paymentStatus} />
            <Row k="Fulfilment Status" v={o.fulfilmentStatus || "—"} />
            <Row k="Payment Mode" v={o.mode} />
            <Row k="Slot" v={o.slot ? o.slot.replace("T", " ") + " IST" : "—"} />
            <Row k="Cashfree Payment ID" v={o.cfPaymentId || "—"} />
            <Row k="Created" v={(o.createdAt || "").replace("T", " ").slice(0, 19)} />
            {o.paidAt && <Row k="Paid" v={o.paidAt.replace("T", " ").slice(0, 19)} />}
            {o.reportPath && (
              <div className="pt-2">
                <a data-testid="admin-download-report" href={`${process.env.REACT_APP_BACKEND_URL}/api/v1/reports/${o.orderId}/${o.reportToken}`} target="_blank" rel="noreferrer"
                  className="inline-flex items-center gap-1.5 text-sm font-semibold text-[#0B2A6B]"><Download className="h-4 w-4" /> Download student PDF</a>
              </div>
            )}
            {o.reportError && <p className="text-xs text-red-600">PDF error: {o.reportError}</p>}
          </dl>
        </div>

        <div className="rounded-2xl border border-slate-200 bg-white p-5">
          <h2 className="text-xs font-bold uppercase tracking-wide text-slate-500">Student</h2>
          <dl className="mt-3 space-y-2 text-sm">
            <Row k={<><User className="h-3.5 w-3.5 inline" /> Name</>} v={o.name} />
            <Row k={<><Phone className="h-3.5 w-3.5 inline" /> Mobile</>} v={o.phone} />
            <Row k={<><Mail className="h-3.5 w-3.5 inline" /> Email</>} v={o.email || "—"} />
            <Row k="Lead ID" v={o.leadId || "—"} />
            {lead && <Row k="Course Interest" v={lead.courseInterest || "—"} />}
            {lead && <Row k="Marks / Rank" v={lead.marks || "—"} />}
            {lead && <Row k="Budget" v={lead.budget || "—"} />}
            {lead && <Row k="State" v={lead.state || "—"} />}
          </dl>
        </div>
      </div>

      <div className="mt-6 rounded-2xl border border-slate-200 bg-white p-5">
        <h2 className="text-xs font-bold uppercase tracking-wide text-slate-500">Update fulfilment</h2>
        <div className="mt-3 grid gap-3 md:grid-cols-3">
          <select data-testid="fulfil-status" value={newStatus} onChange={(e) => setNewStatus(e.target.value)}
            className="rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm">
            {statuses.map((s) => <option key={s} value={s}>{s}</option>)}
          </select>
          <input data-testid="fulfil-assignee" value={assignee} onChange={(e) => setAssignee(e.target.value)}
            placeholder="Assigned counsellor (email or name)"
            className="rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm" />
          <button data-testid="fulfil-save-status" disabled={saving} onClick={() => save({ fulfilmentStatus: newStatus, counsellorAssigned: assignee })}
            className="rounded-lg bg-[#0B2A6B] px-4 py-2 text-sm font-semibold text-white disabled:opacity-50">Save</button>
        </div>
        <div className="mt-4 grid gap-3 md:grid-cols-[1fr_auto]">
          <textarea data-testid="fulfil-note" rows={2} value={note} onChange={(e) => setNote(e.target.value)}
            placeholder="Add a counsellor note (visible to admin only)"
            className="rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm" />
          <button data-testid="fulfil-add-note" disabled={saving || !note.trim()} onClick={() => save({ counsellorNote: note })}
            className="rounded-lg border border-slate-200 bg-white px-4 py-2 text-sm font-semibold text-slate-700 disabled:opacity-40">Add Note</button>
        </div>
        <div className="mt-3 grid gap-3 md:grid-cols-[1fr_auto]">
          <input data-testid="fulfil-attachment" value={attach} onChange={(e) => setAttach(e.target.value)}
            placeholder="Attachment URL (e.g. Google Drive link to the delivered pack)"
            className="rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm" />
          <button data-testid="fulfil-add-attachment" disabled={saving || !attach.trim()} onClick={() => save({ attachmentUrl: attach })}
            className="rounded-lg border border-slate-200 bg-white px-4 py-2 text-sm font-semibold text-slate-700 disabled:opacity-40">Attach</button>
        </div>
      </div>

      {(o.fulfilmentTimeline || []).length > 0 && (
        <div className="mt-6 rounded-2xl border border-slate-200 bg-white p-5">
          <h2 className="text-xs font-bold uppercase tracking-wide text-slate-500">Fulfilment timeline</h2>
          <ul className="mt-3 space-y-2 text-sm">
            {o.fulfilmentTimeline.map((t, i) => (
              <li key={i} className="flex justify-between border-b border-slate-100 pb-2">
                <span className="font-medium text-slate-700">{t.status}</span>
                <span className="text-xs text-slate-400">{t.at?.slice(0, 19).replace("T", " ")} · {t.by}</span>
              </li>
            ))}
          </ul>
        </div>
      )}

      {(o.counsellorNotes || []).length > 0 && (
        <div className="mt-6 rounded-2xl border border-slate-200 bg-white p-5">
          <h2 className="text-xs font-bold uppercase tracking-wide text-slate-500">Counsellor notes</h2>
          <ul className="mt-3 space-y-3 text-sm">
            {o.counsellorNotes.map((n, i) => (
              <li key={i} className="rounded-lg bg-slate-50 p-3">
                <p className="text-slate-700 whitespace-pre-wrap">{n.note}</p>
                <p className="mt-1 text-[11px] text-slate-400">{n.at?.slice(0, 16).replace("T", " ")} · {n.by}</p>
              </li>
            ))}
          </ul>
        </div>
      )}

      {(o.attachments || []).length > 0 && (
        <div className="mt-6 rounded-2xl border border-slate-200 bg-white p-5">
          <h2 className="text-xs font-bold uppercase tracking-wide text-slate-500">Attachments</h2>
          <ul className="mt-3 space-y-2 text-sm">
            {o.attachments.map((a, i) => (
              <li key={i}><a href={a.url} target="_blank" rel="noreferrer" className="text-[#0B2A6B] font-medium break-all">{a.url}</a>
                <span className="ml-2 text-xs text-slate-400">{a.at?.slice(0, 16).replace("T", " ")}</span>
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}

function Row({ k, v }) {
  return (
    <div className="flex justify-between gap-3 border-b border-slate-100 pb-1.5">
      <dt className="text-xs text-slate-500">{k}</dt>
      <dd className="text-sm font-medium text-slate-800 text-right break-words">{v}</dd>
    </div>
  );
}
